import { useMemo } from 'preact/hooks'
import type { Episode, UserEpisode } from '../types/schemas'
import { useMarkEpisode, useUnmarkEpisode } from '../hooks/useEpisodes'
import { Carousel } from './Carousel'

interface SwipeableEpisodeProps {
  episodes: Episode[]
  markedEpisodes: UserEpisode[]
  showId: number
  initialEpisodeId?: number // Default to next unwatched
}

export function SwipeableEpisode({
  episodes,
  markedEpisodes,
  showId,
  initialEpisodeId,
}: SwipeableEpisodeProps) {
  const markEpisode = useMarkEpisode()
  const unmarkEpisode = useUnmarkEpisode()

  // Memoize watched episode IDs to avoid recreating Set on every render
  const watchedEpisodeIds = useMemo(
    () => new Set(markedEpisodes?.filter(me => me.type === 0).map(me => me.episode_id) || []),
    [markedEpisodes],
  )

  const nextUnwatchedIndex = useMemo(
    () => episodes.findIndex(ep => !watchedEpisodeIds.has(ep.id)),
    [episodes, watchedEpisodeIds],
  )

  const initialIndex = useMemo(() => {
    return initialEpisodeId
      ? episodes.findIndex(ep => ep.id === initialEpisodeId)
      : nextUnwatchedIndex
  }, [episodes, initialEpisodeId, nextUnwatchedIndex])

  // Create episode card component
  const EpisodeCard = ({ episode, index }: { episode: Episode; index: number }) => {
    const isWatched = watchedEpisodeIds.has(episode.id)
    const isNext = index === nextUnwatchedIndex

    return (
      <div className="bg-gradient-to-br from-blue-900/40 to-blue-900/20 border border-blue-600/50 rounded-xl p-6 backdrop-blur-sm">
        {/* Header with episode info and action button */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full font-mono text-sm font-semibold border border-blue-500/30">
              {episode.season}x{String(episode.number).padStart(2, '0')}
            </span>
            {isNext && (
              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium border border-blue-500/30">
                Next
              </span>
            )}
          </div>

          {/* Action button */}
          <div className="flex-shrink-0">
            {!isWatched && (
              <button
                onClick={() =>
                  markEpisode.mutate({
                    episodeId: episode.id,
                    showId: showId,
                    type: 0,
                    episodeName: episode.name,
                    season: episode.season,
                    episode: episode.number,
                  })
                }
                disabled={markEpisode.isPending}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg"
              >
                {markEpisode.isPending ? 'Marking...' : 'Mark Watched'}
              </button>
            )}
            {isWatched && (
              <button
                onClick={() => unmarkEpisode.mutate(episode.id)}
                disabled={unmarkEpisode.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700/80 hover:bg-red-600/80 disabled:bg-slate-800 disabled:cursor-not-allowed text-slate-300 hover:text-white font-medium rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                {unmarkEpisode.isPending ? 'Unmarking...' : 'Watched'}
              </button>
            )}
          </div>
        </div>

        {/* Episode content */}
        <div className="flex items-center gap-4">
          {/* Episode info */}
          <div className="flex-1 min-w-0">
            <div className="mb-3">
              <h3 className="text-white font-bold text-xl leading-tight mb-3">{episode.name}</h3>
              {episode.summary && (
                <p className="text-sm text-slate-300 leading-relaxed line-clamp-3">
                  {episode.summary.replace(/<[^>]*>/g, '')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Memoize episode cards to avoid recreating on every render
  const episodeCards = useMemo(() => {
    return episodes.map((episode, index) => (
      <EpisodeCard key={episode.id} episode={episode} index={index} />
    ))
  }, [
    episodes,
    watchedEpisodeIds,
    nextUnwatchedIndex,
    markEpisode.isPending,
    unmarkEpisode.isPending,
    showId,
  ])

  if (episodes.length === 0) return null

  return (
    <Carousel initialIndex={initialIndex >= 0 ? initialIndex : 0} className="mb-8">
      {episodeCards}
    </Carousel>
  )
}
